import { Lang } from './shared/language/language-data.service';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';
import { OrderModule } from './main/order/order.module';
import { SharedModule } from './shared/shared.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { TranslationLoaderService } from './shared/translation/translation-loader.service';
import { AccessModule } from './access/access.module';
import { ManagementModule } from './main/management/management.module';
import { DemoModule } from './main/demo/demo.module';
import { FormsModule } from './main/forms/forms.module';
import { WebshopModule } from './main/webshop/webshop.module';
import { MailModule } from './mail/mail.module';
import { FaqModule } from './main/faq/faq.module';
import { HttpHeadersInterceptor } from './shared/http/http-headers.interceptor';
import { UploadModule } from './main/uploads/upload.module';
import { CompanyProfilesModule } from './main/companyprofiles/company-profiles.module';
import { InvoiceModule } from './main/invoice/invoice.module';
import { CompanyModule } from './main/company/company.module';
import { PaymentModule } from './main/payment/payment.module';
import { UserProfileModule } from './main/userprofile/user-profile.module';
import { BPProfilesModule } from './main/bp-profiles/bp-profiles.module';
import { ReportModule } from '@app/main/report/report.module';
import { GlobalProductModule } from './main/globalproduct/global-product.module';
import { TicketGuideModule } from './main/ticket-guide/ticket-guide.module';
import { GaErrorHandlerService } from './shared/services/ga-error-handler.service';
import { remoteLoggerInitializer } from './shared/initializers/remote-logger-initializer';
import { RemoteLoggerDataService } from './shared/services/logger/remote-logger-data.service';
import { MemoryStorageService } from './shared/services/storage/memory-storage.service';
import { GaEventService } from './shared/services/ga-event.service';
import { HttpDataInterceptor } from './shared/http/http-data.interceptor';
import { HttpAuthInterceptor } from './shared/http/http-auth.interceptor';
import { notyInitializer } from './shared/message/noty-initializer';
import { BrowserVendorService } from './shared/browser/browser-vendor.service';
import { HttpJsonInterceptor } from './shared/http/http-json.interceptor';
import { widthSwitcherInitializer } from './shared/responsive/width-switcher-initializer';
import { UIRouterModule } from '@uirouter/angular';
import { HttpEtagInterceptor } from './shared/http/http-etag.interceptor';
import { ga1MenuInitializer } from './shared/ga1/ga1-menu-initializer';
import { AppComponent } from './app.component';
import { MainParamsComponent } from './main/main-params.component';
import { MainLayoutComponent } from './main/main-layout.component';
import { MainComponent } from './main/main.component';
import { AppStatesService } from './app-states.service';
import { MAIN_STATES, uiRouterConfigFn } from './app-routes.config';
import { AppTransitionService } from './app-transition.service';
import { appInitializer } from './app.initializer';
import { HomeModule } from './main/home/home.module';
import { ticketGuideDialogInitializer } from './shared/services/ticket-guide/ticket-guide-dialog-initializer';
import { reportLayoutSettingsInitializer } from '@app/shared/report/report-layout-settings.initializer';
import { ReportLayoutSettingsService } from '@app/shared/report/report-layout-settings.service';

@NgModule({
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
        deps: [ GaEventService ]
      },
      defaultLanguage: Lang.EN,
      useDefaultLang: true
    }),
    UIRouterModule.forRoot({
      deferIntercept: true,
      useHash: true,
      states: MAIN_STATES,
      config: uiRouterConfigFn
    }),
    HttpClientModule,
    BrowserModule,
    SharedModule,
    HomeModule,
    OrderModule,
    AccessModule,
    ManagementModule,
    DemoModule,
    FormsModule,
    WebshopModule,
    MailModule,
    FaqModule,
    UploadModule,
    CompanyProfilesModule,
    InvoiceModule,
    CompanyModule,
    PaymentModule,
    UserProfileModule,
    BPProfilesModule,
    ReportModule,
    GlobalProductModule,
    TicketGuideModule
  ],
  declarations: [AppComponent, MainParamsComponent, MainComponent, MainLayoutComponent],
  providers: [
    { provide: AppStatesService, useClass: AppStatesService },
    { provide: AppTransitionService, useClass: AppTransitionService },
    { provide: ErrorHandler, useClass: GaErrorHandlerService },
    { provide: Window, useValue: window },
    { provide: HTTP_INTERCEPTORS, useClass: HttpDataInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpAuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpHeadersInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpJsonInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpEtagInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [Injector] },
    { provide: APP_INITIALIZER, useFactory: remoteLoggerInitializer, multi: true, deps: [ MemoryStorageService, RemoteLoggerDataService, Window, DOCUMENT ] },
    { provide: APP_INITIALIZER, useFactory: reportLayoutSettingsInitializer, multi: true, deps: [ReportLayoutSettingsService, Window] },
    { provide: APP_INITIALIZER, useFactory: notyInitializer, multi: true, deps: [BrowserVendorService] },
    { provide: APP_INITIALIZER, useFactory: ga1MenuInitializer, multi: true, deps: [Injector] },
    { provide: APP_INITIALIZER, useFactory: widthSwitcherInitializer, multi: true, deps: [Injector] },
    { provide: APP_INITIALIZER, useFactory: ticketGuideDialogInitializer, multi: true, deps: [Injector] }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }